import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import FaqAccordion from '@/components/feature/FaqAccordion';
import ContactForm from '@/pages/about/components/ContactForm';
import useSeo from '@/hooks/useSeo';
import { brandValues, contactInfo, milestones, testimonials } from '@/mocks/site';
import { faqs, stats } from '@/mocks/topics';

export default function About() {
  useSeo({
    title: '关于我们 - 锐察社社会爆料议题盘点平台',
    description:
      '锐察社是一个聚焦社会爆料与议题盘点的内容平台，以议题为单位梳理行业乱象、消费纠纷与争议事件，坚持呈现多方说法并给出可行方法。',
    keywords: '关于锐察社,社会爆料,议题盘点,社会爆料行业内幕,内容原则',
    path: '/about',
    type: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于锐察社',
        url: 'https://blca.top/about',
        description: '聚焦社会爆料与议题盘点的内容平台。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://blca.top/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://blca.top/about' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-300 bg-background-100">
        <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="font-label text-sm text-foreground-500">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="transition-colors hover:text-primary-700">首页</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-foreground-800">关于我们</li>
            </ol>
          </nav>
          <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <p className="font-label text-xs uppercase tracking-[0.2em] text-primary-600">
                About Us
              </p>
              <h1 className="mt-3 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                锐察社：把社会爆料讲成有脉络的议题
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                我们不追逐单条爆点，而是把一个问题的来龙去脉整理清楚。从行业乱象到消费纠纷，从争议事件到维权方法，我们试着让每一个议题都能被看懂、被使用。
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="h-[220px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[280px]">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20tidy%20editorial%20newsroom%20desk%20with%20notebooks%2C%20a%20coffee%20cup%20and%20a%20warm%20lamp%2C%20warm%20muted%20tones%2C%20documentary%20editorial%20photography%2C%20soft%20natural%20light%2C%20no%20text&width=1000&height=720&seq=rc-about-01&orientation=landscape"
                  alt="锐察社编辑工作台场景配图"
                  title="关于锐察社"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-2 gap-6 rounded-lg border border-background-300 bg-background-100 px-6 py-8 md:grid-cols-4">
          {stats.map((item, index) => (
            <div key={item.label} className="flex flex-col">
              <span
                className={`font-heading text-3xl font-black md:text-4xl ${
                  index === 1 ? 'text-primary-600' : 'text-foreground-950'
                }`}
              >
                {item.value}
              </span>
              <span className="mt-1 font-label text-sm text-foreground-600">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-4 pb-12 md:px-6 md:pb-16">
        <section aria-labelledby="position-title">
          <div className="border-b border-background-300 pb-4">
            <h2 id="position-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
              网站定位与内容原则
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              我们希望做的，是在纷杂的信息里提供一种更清晰的理解方式：讲清现象、标注争议、给出方法。
            </p>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {brandValues.map((value) => (
              <div key={value.title} className="rounded-lg border border-background-300 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className={`${value.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  {value.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="milestone-title" className="mt-14">
          <div className="border-b border-background-300 pb-4">
            <h2 id="milestone-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
              发展理念与历程
            </h2>
            <p className="mt-2 text-sm text-foreground-600">从整理生活经验开始，逐渐长成系统的议题结构。</p>
          </div>
          <ol className="mt-8 border-l-2 border-background-300 pl-6">
            {milestones.map((item) => (
              <li key={item.title} className="relative pb-8 last:pb-0">
                <span className="absolute -left-[31px] top-1 h-3.5 w-3.5 rounded-full border-2 border-background-50 bg-primary-500"></span>
                <p className="font-label text-sm font-medium text-primary-700">{item.time}</p>
                <h3 className="mt-1 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="testimonial-title" className="mt-14">
          <div className="border-b border-background-300 pb-4">
            <h2 id="testimonial-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
              读者怎么说
            </h2>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {testimonials.map((item) => (
              <blockquote
                key={item.name}
                className="flex flex-col rounded-lg border border-background-300 bg-background-100 p-6"
              >
                <i className="ri-double-quotes-l text-2xl text-primary-300" aria-hidden="true"></i>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {item.quote}
                </p>
                <footer className="mt-4 border-t border-background-300 pt-3">
                  <p className="font-heading text-sm font-semibold text-foreground-950">{item.name}</p>
                  <p className="font-label text-xs text-foreground-500">{item.role}</p>
                </footer>
              </blockquote>
            ))}
          </div>
        </section>

        <section id="contact" aria-labelledby="contact-title" className="mt-14 scroll-mt-28">
          <div className="border-b border-background-300 pb-4">
            <h2 id="contact-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
              联系我们
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              无论是补充线索、纠正信息，还是想了解某个议题，都欢迎与我们联系。
            </p>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <ul className="space-y-4">
                {contactInfo.map((item) => (
                  <li
                    key={item.label}
                    className="flex items-start gap-3 rounded-lg border border-background-300 bg-background-100 px-5 py-4"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                      <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
                    </span>
                    <span className="flex flex-col">
                      <span className="font-label text-xs text-foreground-500">{item.label}</span>
                      <span className="mt-0.5 font-heading text-sm font-semibold text-foreground-950">
                        {item.value}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-5">
                <h3 className="font-heading text-base font-bold text-foreground-950">关于隐私</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                  你提供的信息仅用于核实与回复议题相关内容，我们不会将其用于其他用途。
                </p>
              </div>
            </div>
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </section>

        <section aria-labelledby="about-faq-title" className="mt-14">
          <div className="border-b border-background-300 pb-4">
            <h2 id="about-faq-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
              常见问题
            </h2>
          </div>
          <div className="mt-6">
            <FaqAccordion items={faqs} />
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}